<template>
  <div class="flex h-full flex-col gap-2 overflow-hidden">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div v-if="subscriptions.length > 1" class="sticky top-0 z-20 bg-white pb-1 pt-2">
        <InfuseSegments>
          <InfuseSegment
            :active="activeSubscription?.id === subscription.id"
            v-for="subscription in subscriptions"
            :key="subscription.id"
            :name="subscription.name"
            :label="() => subscription.name"
            @click="activeSubscription = subscription"
          />
        </InfuseSegments>
      </div>

      <div class="pb-[4rem]">
        <PermissionsSelector
          :permissions="permissionsPerCategoryFlat.filter((p) => p.subscriptionId === activeSubscription?.id)"
          :enabled-permissions="enabledPermissions"
          :loading="isFetching"
          :search-query="searchQuery"
          :multiple-segments-in-slide-in="subscriptions.length > 1"
          @add="addPermission"
          @remove="removePermission"
          @clear="onClearCategory"
          @readOnly="onReadOnly"
          @all="onAllPermissions"
          @update:searchQuery="debounceSearch"
        >
          <template #actions>
            <InfuseButton @click="onSavePermissions" :loading="isMutating" :disabled="isMutating">
              {{ t('action.save') }}
            </InfuseButton>
          </template>
        </PermissionsSelector>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AccessType } from '@infuse/api/iam/permission';
import { useAssetLevel, usePermissionsSubscriptionFilter } from '@infuse/iam/composables';
import { usePermissions } from '@infuse/iam/permission/queries';
import { PermissionsSelector } from '@infuse/iam/permission/ui';
import { useAddPermissionToRole } from '@infuse/iam/role/mutations';
import { useRolePermissions } from '@infuse/iam/role/queries';
import { Role } from '@infuse/iam/role/type';
import { InfuseButton, InfuseSegment, InfuseSegments } from '@infuse/ui/components';
import { debounce } from 'lodash';
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  model: Role;
  isLoading?: boolean;
}>();

const { getLevel } = useAssetLevel();

const level = computed(() => getLevel(props.model));
const parentId = computed(() => props.model.organisationId || props.model.tenantId);

const { permissions: rolePermissions, fetchRoleId, isLoading: isLoadingRolePermissions } = useRolePermissions();

const {
  getPermissionsOfCategory,
  permissionsPerCategory,
  permissions,
  isLoading: isLoadingPermissions,
  searchQuery
} = usePermissions(level.value, parentId.value);

const { subscriptions, activeSubscription } = usePermissionsSubscriptionFilter(permissions);

const { mutateAsync: addPermissionsToRole, isPending: isAddingPermissions } = useAddPermissionToRole(
  level.value,
  parentId.value
);

const isFetching = computed(() => props.isLoading || isLoadingRolePermissions.value || isLoadingPermissions.value);
const isMutating = computed(() => isAddingPermissions.value);

watch(
  () => props.model,
  (role) => {
    if (!role) {
      return;
    }

    fetchRoleId.value = role.id;
  },
  { immediate: true }
);

watch(
  () => rolePermissions.value,
  () => {
    for (const permission of rolePermissions.value) {
      addPermission(permission.id);
    }
  },
  { immediate: true }
);

const debounceSearch = debounce((query: string) => {
  if (query !== searchQuery.value) {
    searchQuery.value = query;
  }
}, 500);

let enabledPermissions = reactive<string[]>([]);

function addPermission(permissionId: string) {
  if (enabledPermissions.includes(permissionId)) {
    return;
  }

  enabledPermissions.push(permissionId);
}

function removePermission(permissionId: string) {
  const index = enabledPermissions.indexOf(permissionId);
  if (index > -1) {
    enabledPermissions.splice(index, 1);
  }
}

function onClearCategory(category: string) {
  const categoryPermissions = getPermissionsOfCategory(category);

  for (const categoryPermission of categoryPermissions) {
    removePermission(categoryPermission.id);
  }
}

function onReadOnly(category: string) {
  onClearCategory(category);
  const categoryPermissions = getPermissionsOfCategory(category).filter(
    (p) => p.accessType === AccessType.READ || p.accessType === AccessType.SEARCH
  );

  for (const categoryPermission of categoryPermissions) {
    addPermission(categoryPermission.id);
  }
}

function onAllPermissions(category: string) {
  const categoryPermissions = getPermissionsOfCategory(category);

  for (const categoryPermission of categoryPermissions) {
    addPermission(categoryPermission.id);
  }
}

async function onSavePermissions() {
  await addPermissionsToRole({ roleId: props.model.id, permissionIds: enabledPermissions, replace: true });
}

const permissionsPerCategoryFlat = computed(() => {
  // Flatten all permissions for PermissionsSelector
  return permissionsPerCategory.value.flatMap((cat) => [
    ...cat.objectTypeActions.flatMap((a) => a.actions),
    ...cat.objectTypeCrud.flatMap((c) => c.crud)
  ]);
});
</script>
